// BatiAssistTarifsPage.jsx
// Page Tarifs BatiAssist dans le contexte Espace Pro Abracadabati

import { motion } from 'framer-motion';
import { 
  CreditCard, ArrowRight, CheckCircle, Star, 
  Zap, Users, Bot, Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const plans = [
  {
    name: "Start",
    price: "149",
    description: "Pour démarrer la digitalisation",
    features: [
      "Cockpit Bâtiment complet",
      "Devis & Factures",
      "Pipeline commercial",
      "Application mobile",
      "Support email",
    ],
    cta: "Commencer",
    popular: false,
  },
  {
    name: "Pro",
    price: "349",
    description: "Pour les artisans établis",
    features: [
      "Tout de Start +",
      "Automatisation emails/SMS",
      "Assistant IA intégré",
      "Signature électronique",
      "Support prioritaire",
      "Formation incluse",
    ],
    cta: "Choisir Pro",
    popular: true,
  },
  {
    name: "Croissance",
    price: "1499",
    description: "Pour scaler ton activité",
    features: [
      "Tout de Pro +",
      "Super Assistant dédié",
      "40h/mois d'assistance humaine",
      "Gestion complète admin",
      "Account manager dédié",
      "Onboarding personnalisé",
    ],
    cta: "Contacter",
    popular: false,
  },
];

export default function BatiAssistTarifsPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="min-h-screen bg-white" data-testid="batiassist-tarifs-page">
        <UnifiedHeader />
        <EspaceProNav />
      
      <main>
        {/* Hero */}
        <section className="pt-16 pb-16 md:pt-20 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <CreditCard className="w-4 h-4" />
                Tarifs BatiAssist
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Des offres adaptées à{' '}
                <span className="text-[#F97316]">chaque étape</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10">
                Que tu sois artisan solo ou patron d'une équipe de 20 personnes, 
                nous avons une solution qui correspond à tes besoins et ton budget.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {plans.map((plan, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`relative p-6 md:p-8 rounded-3xl ${
                    plan.popular 
                      ? 'bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] text-white scale-105 shadow-2xl' 
                      : 'bg-slate-50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-[#F97316] text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 fill-white" /> POPULAIRE
                      </span>
                    </div>
                  )}
                  
                  <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-slate-800'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-4 ${plan.popular ? 'text-white/70' : 'text-slate-500'}`}>
                    {plan.description}
                  </p>
                  
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-[#1E3A5F]'}`}>
                      {plan.price}€
                    </span>
                    <span className={plan.popular ? 'text-white/70' : 'text-slate-500'}>/mois</span>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-3">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 ${
                          plan.popular ? 'text-[#F97316]' : 'text-green-500'
                        }`} />
                        <span className={plan.popular ? 'text-white/90' : 'text-slate-600'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    <Button className={`w-full py-6 font-bold rounded-xl ${
                      plan.popular 
                        ? 'bg-[#F97316] hover:bg-[#EA580C] text-white' 
                        : 'bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white'
                    }`}>
                      {plan.cta}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Guarantee */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Satisfait ou remboursé
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Tu as <strong>30 jours</strong> pour tester BatiAssist. Si tu n'es pas convaincu, 
                nous te remboursons intégralement, sans poser de questions.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F]">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Encore des questions ?
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Réserve un diagnostic gratuit de 60 minutes pour discuter de ton projet.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-full px-10 py-6 text-lg">
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
